import { useMemo } from "react";
import type { AppCatalog } from "../services/app-catalog-api";

/**
 * FR-064: what a company actually bought. Base sells packages — "Digital Workspace - Advance",
 * "Employee Happiness - Basic" — not individual app names, and this is how CO Members already
 * describe their customers in practice (every company in live data was tagged this way).
 *
 * Grouped by family with the tiers as radio-style choices, because the tiers within a family are
 * mutually exclusive: nobody buys both Digital Workspace Basic and Premium. Clicking the selected
 * tier again clears the family, which is the only way to deselect a radio.
 */
export function PackagePicker({
  catalog,
  selected,
  onChange,
}: {
  catalog: AppCatalog | null;
  selected: string[];
  onChange: (codes: string[]) => void;
}) {
  /** Packages grouped by family, in catalogue order. */
  const families = useMemo(() => {
    const byFamily = new Map<string, AppCatalog["packages"]>();
    for (const pkg of catalog?.packages ?? []) {
      byFamily.set(pkg.family, [...(byFamily.get(pkg.family) ?? []), pkg]);
    }
    return [...byFamily.entries()];
  }, [catalog]);

  function pick(family: AppCatalog["packages"], code: string) {
    const familyCodes = family.map((p) => p.code);
    const others = selected.filter((c) => !familyCodes.includes(c));
    // Re-clicking the current tier clears this family rather than re-selecting it.
    onChange(selected.includes(code) ? others : [...others, code]);
  }

  if (!catalog) return <p className="text-sm text-body">Loading the Base package list…</p>;

  return (
    <div className="flex flex-col gap-3">
      {families.map(([family, packages]) => (
        <div key={family} className="flex flex-wrap items-center gap-2">
          <span className="w-48 shrink-0 text-sm text-body">{family}</span>
          {packages.map((pkg) => {
            const isSelected = selected.includes(pkg.code);
            return (
              <button
                key={pkg.code}
                type="button"
                aria-pressed={isSelected}
                onClick={() => pick(packages, pkg.code)}
                title={`${pkg.apps.length} apps: ${pkg.apps.join(", ")}`}
                className={`rounded border px-3 py-1 text-sm ${
                  isSelected
                    ? "border-brand bg-brand text-surface"
                    : "border-surface-alt bg-surface text-body"
                }`}
              >
                {/* A family with a single package (Finance Management) has no tier to name. */}
                {pkg.tier ?? pkg.label}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
